import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, Plus, Minus, Edit3, Trash2, ScanLine, Camera,
  Package, AlertTriangle, RefreshCw, Calendar, Hash, UserMinus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";
import BarcodeScanner from "../components/BarcodeScanner";
import { unmojibakeWithDiagnostic } from "../lib/scan-unmojibake";

/**
 * Phase 87bd — Mode Comptoir Distributeur
 *
 * Page tablette-friendly pour saisir/corriger l'état physique d'un distributeur
 * sans avoir à passer par les pages Stocks / Inventaires.
 *
 * Workflow :
 * 1. L'opérateur ouvre /distributeurs/:id/comptoir
 * 2. Voit les 15 slots du distributeur en colonne (1 card par slot)
 * 3. Pour chaque lot actif dans le slot : boutons gros doigt (−1, +1, ✏️, 🗑)
 * 4. Bouton « + Nouveau lot dans ce slot » → dialog scan QR ou saisie SKU+lot+DLC
 * 5. Zéro scan obligatoire, mais scan possible en bonus (caméra ou HID Inateck)
 *
 * Endpoints utilisés (tous existants) :
 * - GET /distributors/{id}
 * - GET /lots (filtrage par distributor_id côté client)
 * - GET /products
 * - POST /movements (type=ADJUST écrase qty / IN/OUT incrémente)
 * - POST /lots (création nouveau lot dans le distributeur+slot)
 * - POST /scan/mark-empty (vider un lot)
 * - POST /scan/resolve (parsing QR pour pré-remplir le formulaire « nouveau lot »)
 */
export default function DistributorComptoir() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [distributor, setDistributor] = useState(null);
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit qty dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editLot, setEditLot] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Nouveau lot dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    product_id: "",
    lot_number: "",
    quantity: 1,
    dlc: "",
    emplacement_number: "",
    barquette_index: "",
  });
  const [newSaving, setNewSaving] = useState(false);
  // Phase 101dk (Feb 2026) — Lots Conthey dispo pour transfert (au lieu de créer un LOT-fantaisie)
  // Phase 101do (Feb 2026) — On stocke aussi `depot_name` retourné par le backend
  // pour distinguer « Stock Conthey » (dépôt central) de « Distributeur de Conthey »
  // (machine physique) dans les libellés UI.
  const [contheyLots, setContheyLots] = useState([]);
  const [contheyLotsLoading, setContheyLotsLoading] = useState(false);
  const [sourceDepotName, setSourceDepotName] = useState("Stock Conthey");
  const [sourceLotId, setSourceLotId] = useState("");  // "" | "manual" | "<lot_id>"

  // Scan
  const [cameraOpen, setCameraOpen] = useState(false);
  const [hidInput, setHidInput] = useState("");
  const hidRef = useRef(null);
  const [resolving, setResolving] = useState(false);

  // Filtre slot (utile sur les distributeurs avec 30+ slots)
  const [slotFilter, setSlotFilter] = useState("all");

  // Phase 101dt (Feb 2026) — Stock Conthey ID pour transférer les décréments
  // (au lieu de détruire les barquettes via ADJUST négatif). Résolu au load
  // via GET /settings. Si null (config incomplète) OU si le distributeur EN
  // COURS est Conthey lui-même → fallback ADJUST direct.
  const [contheyDepotId, setContheyDepotId] = useState(null);
  const isViewingConthey = contheyDepotId && contheyDepotId === id;

  // Phase 87be — Sortie partenaire (vente directe / emprunt / casse signalée)
  const [partners, setPartners] = useState([]);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    partner_id: "", quantity: 1, reason: "VENTE_DIRECTE", notes: "", target_lot: null,
  });
  const [partnerSaving, setPartnerSaving] = useState(false);

  const productMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products],
  );

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, lRes, pRes, partRes, sRes] = await Promise.all([
        api.get("/distributors"),
        // Phase 98ap — Filtre serveur pour éviter la troncature à 5000
        api.get(`/lots?distributor_id=${id}`),
        api.get("/products"),
        api.get("/partners").catch(() => ({ data: [] })),  // gracieux si role non-admin
        api.get("/settings").catch(() => ({ data: {} })),  // Phase 101dt — Stock Conthey ID
      ]);
      const target = (dRes.data || []).find((d) => d.id === id);
      if (!target) {
        toast.error("Distributeur introuvable");
        navigate("/distributeurs");
        return;
      }
      setDistributor(target);
      setLots(lRes.data);
      setProducts(pRes.data);
      // Phase 87be — partenaires autorisés sur ce distributeur (filtre dans le dialog)
      setPartners((partRes.data || []).filter(
        (p) => !p.archived && (
          !p.assigned_distributor_ids?.length || p.assigned_distributor_ids.includes(id)
        )
      ));
      // Phase 101dt (Feb 2026) — ID du dépôt central Conthey pour les retours
      setContheyDepotId(sRes.data?.production_depot_id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Grouper les lots par emplacement
  const lotsBySlot = useMemo(() => {
    const map = new Map();
    for (const lot of lots) {
      const slot = lot.emplacement_number ?? "none";
      if (!map.has(slot)) map.set(slot, []);
      map.get(slot).push(lot);
    }
    // Tri intra-slot : DLC croissante (FIFO) puis lot_number
    for (const list of map.values()) {
      list.sort((a, b) => {
        const da = a.dlc || "9999-12-31";
        const db = b.dlc || "9999-12-31";
        if (da !== db) return da.localeCompare(db);
        return String(a.lot_number).localeCompare(String(b.lot_number));
      });
    }
    return map;
  }, [lots]);

  // Liste des slots à afficher (selon le filtre)
  const slotsToShow = useMemo(() => {
    if (!distributor) return [];
    const all = (distributor.emplacements || []).slice().sort((a, b) => a - b);
    if (slotFilter === "occupied") {
      return all.filter((s) => {
        const arr = lotsBySlot.get(s) || [];
        return arr.some((l) => (l.quantity || 0) > 0);
      });
    }
    if (slotFilter === "empty") {
      return all.filter((s) => {
        const arr = lotsBySlot.get(s) || [];
        return arr.every((l) => (l.quantity || 0) <= 0);
      });
    }
    if (slotFilter === "expiring") {
      return all.filter((s) => {
        const arr = lotsBySlot.get(s) || [];
        return arr.some((l) => {
          if (!l.dlc || (l.quantity || 0) <= 0) return false;
          const d = new Date(l.dlc);
          const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
          return diff <= 3;
        });
      });
    }
    return all;
  }, [distributor, lotsBySlot, slotFilter]);

  // Stats globales
  const stats = useMemo(() => {
    const activeLots = lots.filter((l) => (l.quantity || 0) > 0);
    const totalQty = activeLots.reduce((s, l) => s + (l.quantity || 0), 0);
    const occupiedSlots = new Set(activeLots.map((l) => l.emplacement_number).filter((x) => x != null)).size;
    const totalSlots = distributor?.emplacements?.length || 0;
    const expiringSoon = activeLots.filter((l) => {
      if (!l.dlc) return false;
      const d = new Date(l.dlc);
      const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
      return diff <= 3;
    }).length;
    return { totalQty, occupiedSlots, totalSlots, expiringSoon, activeLotCount: activeLots.length };
  }, [lots, distributor]);

  // ============== Actions ==============

  const adjustBy = async (lot, delta) => {
    const newQty = Math.max(0, (lot.quantity || 0) + delta);
    if (newQty === lot.quantity) return;

    // Phase 101dt (Feb 2026) — Décrément = transfert vers Stock Conthey
    // (au lieu d'un ADJUST négatif qui détruisait les barquettes). Les
    // barquettes retournent physiquement à Conthey où elles peuvent être
    // redispatchées vers un autre distributeur.
    // Exceptions (fallback ADJUST) : Conthey non résolu, ou on est SUR Conthey.
    if (delta < 0 && contheyDepotId && !isViewingConthey) {
      const qtyToTransfer = -delta;
      try {
        await api.post("/transfers", {
          lot_id: lot.id,
          quantity: qtyToTransfer,
          to_distributor_id: contheyDepotId,
          to_emplacement_number: null,
          reason: `Retour Stock Conthey — Comptoir ${distributor?.name || ""}`,
        });
        // Mise à jour optimiste locale : décrémente le lot au distributeur
        setLots((prev) => prev.map((l) => l.id === lot.id ? { ...l, quantity: newQty } : l));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur transfert Conthey");
      }
      return;
    }

    // Cas +1 ou fallback (Conthey non configuré / auto-référence)
    try {
      await api.post("/movements", {
        product_id: lot.product_id,
        lot_id: lot.id,
        type: "ADJUST",
        quantity: newQty,
        reason: delta > 0 ? "Comptoir +1" : "Comptoir −1",
        distributor_id: id,
      });
      // Mise à jour optimiste
      setLots((prev) => prev.map((l) => l.id === lot.id ? { ...l, quantity: newQty } : l));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'ajustement");
    }
  };

  const openEditQty = (lot) => {
    setEditLot(lot);
    setEditQty(String(lot.quantity ?? 0));
    setEditOpen(true);
  };

  const saveEditQty = async () => {
    const n = Number(editQty);
    if (isNaN(n) || n < 0) { toast.error("Quantité invalide"); return; }
    setEditSaving(true);
    try {
      const oldQty = editLot.quantity || 0;
      const diff = n - oldQty;
      // Phase 101dt — Saisie qty à la baisse = transfert vers Stock Conthey
      // (au lieu d'un ADJUST qui détruisait la diff). Sauf si Conthey non
      // résolu ou on est SUR Conthey → fallback ADJUST.
      if (diff < 0 && contheyDepotId && !isViewingConthey) {
        const qtyToTransfer = -diff;
        await api.post("/transfers", {
          lot_id: editLot.id,
          quantity: qtyToTransfer,
          to_distributor_id: contheyDepotId,
          to_emplacement_number: null,
          reason: `Retour Stock Conthey — Comptoir saisie qty exacte (${oldQty}→${n})`,
        });
        setLots((prev) => prev.map((l) => l.id === editLot.id ? { ...l, quantity: n } : l));
        toast.success(`${oldQty - n} barquette(s) transférées à Stock Conthey · ${editLot.lot_number}`);
      } else {
        // Cas augmentation, égalité, ou fallback → ADJUST classique
        await api.post("/movements", {
          product_id: editLot.product_id,
          lot_id: editLot.id,
          type: "ADJUST",
          quantity: n,
          reason: "Comptoir — saisie qty exacte",
          distributor_id: id,
        });
        setLots((prev) => prev.map((l) => l.id === editLot.id ? { ...l, quantity: n } : l));
        toast.success(`Qty ${editLot.lot_number} → ${n}`);
      }
      setEditOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setEditSaving(false);
    }
  };

  const markEmpty = async (lot) => {
    const ok = await confirm({
      title: "Marquer ce lot vide ?",
      description: `${productMap[lot.product_id]?.name || lot.lot_number} — un mouvement « Écart inventaire » sera enregistré pour la traçabilité HACCP.`,
      confirmText: "Marquer vide",
    });
    if (!ok) return;
    try {
      await api.post("/scan/mark-empty", { lot_id: lot.id });
      toast.success("Slot libéré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 87be — Sortie d'une barquette pour un partenaire (vente directe, prêt…)
  const openPartnerWithdrawal = (lot) => {
    if (partners.length === 0) {
      toast.error("Aucun partenaire enregistré — créez-en un dans /partenaires d'abord");
      return;
    }
    setPartnerForm({
      partner_id: "",
      quantity: 1,
      reason: "VENTE_DIRECTE",
      notes: "",
      target_lot: lot,
    });
    setPartnerOpen(true);
  };

  const savePartnerWithdrawal = async () => {
    if (!partnerForm.partner_id) { toast.error("Choisissez un partenaire"); return; }
    const q = Number(partnerForm.quantity);
    if (!q || q <= 0) { toast.error("Quantité > 0"); return; }
    const lot = partnerForm.target_lot;
    if (q > (lot.quantity || 0)) {
      toast.error(`Seulement ${lot.quantity} en stock dans ce lot`);
      return;
    }
    setPartnerSaving(true);
    try {
      await api.post("/partner-withdrawals", {
        partner_id: partnerForm.partner_id,
        distributor_id: id,
        product_id: lot.product_id,
        lot_id: lot.id,
        quantity: q,
        reason: partnerForm.reason,
        notes: partnerForm.notes || null,
      });
      const partnerName = partners.find((p) => p.id === partnerForm.partner_id)?.name || "partenaire";
      toast.success(`Retrait ${partnerName} enregistré · stock −${q}`);
      setPartnerOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setPartnerSaving(false);
    }
  };

  // ============== Nouveau lot ==============

  const openNewLot = (slot) => {
    setNewForm({
      product_id: "",
      lot_number: "",
      quantity: 1,
      dlc: "",
      emplacement_number: slot && slot !== "none" ? String(slot) : "",
      barquette_index: "",
    });
    setContheyLots([]);
    setSourceLotId("");
    setNewOpen(true);
  };

  const computeAutoDlc = (productId) => {
    const p = productMap[productId];
    if (!p?.shelf_life_days) return "";
    const d = new Date();
    d.setDate(d.getDate() + p.shelf_life_days);
    return d.toISOString().slice(0, 10);
  };

  const onPickNewProduct = async (productId) => {
    setNewForm((f) => ({
      ...f,
      product_id: productId,
      dlc: f.dlc || computeAutoDlc(productId),
      lot_number: "",
    }));
    // Phase 101dk (Feb 2026) — Charger les lots dispos à Conthey pour ce produit
    // (utilise l'endpoint existant /moma-refills/available-lots + include_dedicated=true
    // pour voir aussi les lots réservés à des commandes clients — utile pour visibilité
    // opérateur, le comptoir peut les prendre en connaissance de cause).
    setSourceLotId("");
    setContheyLots([]);
    if (!productId) return;
    setContheyLotsLoading(true);
    try {
      const { data } = await api.get(
        `/moma-refills/available-lots/${productId}?include_dedicated=true`
      );
      setContheyLots(data.lots || []);
      if (data.depot_name) setSourceDepotName(data.depot_name);
    } catch (e) {
      // Silencieux : si l'endpoint échoue on retombe sur le mode manuel
      setContheyLots([]);
    } finally {
      setContheyLotsLoading(false);
    }
  };

  // Phase 101dk — Bascule vers un lot Conthey existant (auto-fill lot_number+DLC)
  const pickSourceLot = (lotIdOrManual) => {
    setSourceLotId(lotIdOrManual);
    if (lotIdOrManual === "" || lotIdOrManual === "manual") {
      // Mode manuel : on ne touche pas au formulaire (utilisateur saisit)
      setNewForm((f) => ({ ...f, lot_number: "", dlc: f.dlc }));
      return;
    }
    const lot = contheyLots.find((l) => l.id === lotIdOrManual);
    if (lot) {
      setNewForm((f) => ({
        ...f,
        lot_number: lot.lot_number || "",
        dlc: lot.dlc || f.dlc,
      }));
    }
  };

  const submitNewLot = async () => {
    if (!newForm.product_id) { toast.error("Choisissez un produit"); return; }
    const q = Number(newForm.quantity);
    if (isNaN(q) || q <= 0) { toast.error("Quantité doit être ≥ 1"); return; }

    // Phase 101dk (Feb 2026) — 2 branches :
    //   • sourceLotId défini + non-manual → TRANSFERT propre depuis Conthey
    //   • sinon → création directe (avec validation stricte lot_number obligatoire)
    setNewSaving(true);
    try {
      if (sourceLotId && sourceLotId !== "manual") {
        // === TRANSFERT Conthey → distributeur ===
        // Utilise POST /transfers (fusion auto si lot identique déjà présent au slot)
        const src = contheyLots.find((l) => l.id === sourceLotId);
        if (!src) { toast.error("Lot source introuvable"); return; }
        if (q > Number(src.quantity)) {
          toast.error(`Quantité demandée (${q}) > disponible à Conthey (${src.quantity})`);
          return;
        }
        await api.post("/transfers", {
          lot_id: sourceLotId,
          quantity: q,
          to_distributor_id: id,
          to_emplacement_number: newForm.emplacement_number ? Number(newForm.emplacement_number) : null,
          reason: `Comptoir · reprise lot ${src.lot_number} depuis ${sourceDepotName}`,
        });
        toast.success(`Transfert ${sourceDepotName} → ${distributor.name} · ${q}× ${productMap[newForm.product_id]?.name || ""} (lot ${src.lot_number})`);
      } else {
        // === CRÉATION NOUVEAU LOT (aucun lot Conthey ou création manuelle) ===
        // Phase 101dk : plus de fallback `LOT-XXXXX` — validation stricte
        const lotNumber = newForm.lot_number.trim();
        if (!lotNumber) {
          toast.error("N° de lot obligatoire — reprenez un lot Conthey existant ou saisissez le n° manuellement.");
          return;
        }
        const payload = {
          product_id: newForm.product_id,
          lot_number: lotNumber,
          quantity: q,
          dlc: newForm.dlc || null,
          distributor_id: id,
          emplacement_number: newForm.emplacement_number ? Number(newForm.emplacement_number) : null,
          barquette_index: newForm.barquette_index ? Number(newForm.barquette_index) : null,
          notes: "Saisi via mode Comptoir (création manuelle)",
        };
        await api.post("/lots", payload);
        toast.success(`Lot ${lotNumber} créé · ${productMap[newForm.product_id]?.name || ""}`);
      }
      setNewOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création lot");
    } finally {
      setNewSaving(false);
    }
  };

  // ============== Scan ==============

  const resolveScan = async (rawCode) => {
    let code = (rawCode || "").trim();
    if (!code) return;
    const diag = unmojibakeWithDiagnostic(code);
    if (diag.wasCorrected) {
      code = diag.corrected;
      toast.warning("Scanner clavier US — payload corrigé auto", { duration: 3000 });
    }

    setResolving(true);
    try {
      // EAN13 pur ? résolution locale (1 produit = pré-rempli direct)
      if (/^\d{12,13}$/.test(code)) {
        const base = code.length === 13 ? code.slice(0, 12) : code;
        const matches = products.filter((p) => (p.ean13_base || "").trim() === base);
        if (matches.length === 0) {
          toast.error(`EAN ${code} inconnu`);
          return;
        }
        // Choisi le premier si 1 seul, sinon force le user à choisir manuellement
        if (matches.length === 1) {
          onPickNewProduct(matches[0].id);
          toast.success(`${matches[0].name} sélectionné`);
          if (!newOpen) openNewLot(""); // ouvre le dialog "nouveau lot" sans slot pré-sélectionné
          return;
        }
        toast.info(`EAN partagé par ${matches.length} produits — choisissez dans la liste`);
        if (!newOpen) openNewLot("");
        return;
      }
      // QR / URL trace → backend
      const { data } = await api.post("/scan/resolve", { payload: code });
      const lot = data?.lot;
      const product = data?.product;
      if (!product) {
        toast.error("Produit introuvable");
        return;
      }
      // Cherche si le lot existe déjà dans ce distributeur → on l'ouvre en édition qty
      const existingLot = lots.find((l) => l.id === lot.id);
      if (existingLot) {
        toast.success(`${product.name} — édition de la qty`);
        openEditQty(existingLot);
        return;
      }
      // Sinon → pré-remplit le dialog "nouveau lot"
      setNewForm({
        product_id: product.id,
        lot_number: lot?.lot_number || "",
        quantity: 1,
        dlc: lot?.dlc ? lot.dlc.slice(0, 10) : computeAutoDlc(product.id),
        emplacement_number: lot?.emplacement_number ? String(lot.emplacement_number) : "",
        barquette_index: lot?.barquette_index ? String(lot.barquette_index) : "",
      });
      setNewOpen(true);
      toast.success(`${product.name} — pré-rempli, complétez puis enregistrez`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code illisible");
    } finally {
      setResolving(false);
    }
  };

  // ============== Render ==============

  if (loading || !distributor) {
    return <div className="text-[#575A57] p-6">Chargement…</div>;
  }

  return (
    <div className="space-y-4 pb-20" data-testid="comptoir-page">
      {/* Header sticky */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4 sticky top-0 z-20 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/distributeurs/${id}`)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#575A57]">Mode Comptoir</div>
              <div className="font-heading text-lg font-medium truncate" data-testid="comptoir-dist-name">{distributor.name}</div>
              {distributor.code && (
                <div className="text-xs font-mono text-[#575A57]">{distributor.code}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/aide/scanner" target="_blank" rel="noopener noreferrer" className="text-xs text-[#575A57] hover:text-[#1F4E3D] hover:underline flex items-center gap-1">
              <ScanLine className="w-3 h-3" /> Aide scanner
            </Link>
            <Button variant="outline" size="sm" onClick={load} data-testid="comptoir-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Rafraîchir
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCameraOpen(true)} data-testid="comptoir-open-camera">
              <Camera className="w-4 h-4 mr-1.5" /> Caméra
            </Button>
            <Button
              size="sm"
              onClick={() => openNewLot("")}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="comptoir-new-lot-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau lot
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mt-3">
          <StatChip label="Slots" value={`${stats.occupiedSlots}/${stats.totalSlots}`} testid="stat-slots" />
          <StatChip label="Lots actifs" value={stats.activeLotCount} testid="stat-lots" />
          <StatChip label="Unités totales" value={stats.totalQty} testid="stat-qty" />
          <StatChip label="DLC ≤ 3j" value={stats.expiringSoon} variant={stats.expiringSoon > 0 ? "warn" : "default"} testid="stat-expiring" />
        </div>

        {/* HID scan input + filtre */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <form
            onSubmit={(e) => { e.preventDefault(); const v = hidInput; setHidInput(""); resolveScan(v); }}
            className="flex-1 min-w-[280px] flex items-center gap-2 bg-[#1F4E3D]/5 border border-[#1F4E3D]/30 rounded-md px-2 py-1"
          >
            <ScanLine className={`w-4 h-4 text-[#1F4E3D] shrink-0 ${resolving ? "animate-pulse" : ""}`} />
            <Input
              ref={hidRef}
              value={hidInput}
              onChange={(e) => setHidInput(e.target.value)}
              placeholder="Scannez un QR étiquette ou EAN13 — pré-remplit auto le formulaire"
              className="font-mono bg-white text-sm border-0 focus-visible:ring-0 h-8"
              disabled={resolving}
              data-testid="comptoir-hid-input"
            />
          </form>
          <Select value={slotFilter} onValueChange={setSlotFilter}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="comptoir-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les slots</SelectItem>
              <SelectItem value="occupied">Occupés</SelectItem>
              <SelectItem value="empty">Vides</SelectItem>
              <SelectItem value="expiring">DLC ≤ 3j</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Liste des slots */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="comptoir-slots-grid">
        {slotsToShow.length === 0 ? (
          <div className="col-span-full text-center text-[#575A57] py-10 bg-white border border-dashed border-[#E5E5E0] rounded-lg">
            <Package className="w-8 h-8 mx-auto mb-2 text-[#A3A6A3]" />
            <div className="font-medium">Aucun slot correspondant au filtre</div>
          </div>
        ) : slotsToShow.map((slot) => {
          const slotLots = (lotsBySlot.get(slot) || []).filter((l) => (l.quantity || 0) > 0);
          const capacity = distributor.emplacement_capacities?.[String(slot)] ?? null;
          const used = slotLots.reduce((s, l) => s + (l.quantity || 0), 0);
          return (
            <SlotCard
              key={slot}
              slot={slot}
              capacity={capacity}
              used={used}
              lots={slotLots}
              productMap={productMap}
              onAdjust={adjustBy}
              onEdit={openEditQty}
              onEmpty={markEmpty}
              onPartner={openPartnerWithdrawal}
              onAddLot={() => openNewLot(slot)}
              partnersAvailable={partners.length > 0}
            />
          );
        })}
      </div>

      {/* Dialog édition qty */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm" data-testid="edit-qty-dialog">
          <DialogHeader>
            <DialogTitle>Saisir la quantité exacte</DialogTitle>
            <DialogDescription>
              {editLot ? (
                <>
                  <span className="font-medium">{productMap[editLot?.product_id]?.name}</span>
                  <br/>
                  Lot <span className="font-mono">{editLot?.lot_number}</span>
                  {editLot?.emplacement_number ? <> · Slot {editLot.emplacement_number}</> : null}
                </>
              ) : "Sélectionnez un lot"}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="mb-1.5 block">Quantité comptée</Label>
            <Input
              type="number"
              min="0"
              value={editQty}
              onChange={(e) => setEditQty(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveEditQty(); }}
              autoFocus
              className="text-2xl font-mono text-center h-14"
              data-testid="edit-qty-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>Annuler</Button>
            <Button onClick={saveEditQty} disabled={editSaving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="edit-qty-save">
              {editSaving ? "…" : "Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog nouveau lot */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md" data-testid="new-lot-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau lot dans le distributeur</DialogTitle>
            <DialogDescription>
              Si vous avez un QR d&apos;étiquette HACCP imprimée, scannez-le pour pré-remplir tous les champs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCameraOpen(true)}
              className="w-full"
              data-testid="new-lot-scan-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Scanner QR / EAN13 pour pré-remplir
            </Button>
            <div>
              <Label className="mb-1.5 block">Produit *</Label>
              <Select value={newForm.product_id} onValueChange={onPickNewProduct}>
                <SelectTrigger data-testid="new-lot-product">
                  <SelectValue placeholder="Sélectionner un produit…" />
                </SelectTrigger>
                <SelectContent>
                  {products.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} <span className="text-xs text-[#A3A6A3] font-mono ml-1">({p.sku})</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phase 101dk (Feb 2026) — Picker lots Conthey existants pour éviter
                de créer un lot fantaisie LOT-XXXX quand le produit est déjà en
                stock à Conthey. C'est ce que l'utilisatrice appelait « chercher
                le lot manuellement dans la liste ». */}
            {newForm.product_id && (
              <div className="border border-[#E5E5E0] rounded-md p-2 bg-[#F9F9F6]" data-testid="new-lot-conthey-picker">
                <Label className="mb-1.5 block text-xs uppercase tracking-wider text-[#1F4E3D]">
                  Source du lot ({sourceDepotName})
                </Label>
                {contheyLotsLoading ? (
                  <div className="text-xs text-[#575A57]">Chargement des lots à {sourceDepotName}…</div>
                ) : contheyLots.length === 0 ? (
                  <div className="text-xs text-[#8C6B2F] italic">
                    ⚠ Aucun lot pour ce produit à {sourceDepotName} — saisissez un n° de lot manuellement ci-dessous.
                  </div>
                ) : (
                  <Select value={sourceLotId} onValueChange={pickSourceLot}>
                    <SelectTrigger data-testid="new-lot-conthey-select">
                      <SelectValue placeholder={`Choisir un lot à reprendre depuis ${sourceDepotName}…`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {contheyLots.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          <span className="font-mono">{l.lot_number}</span>
                          {" · "}
                          {l.quantity}×
                          {l.dlc && <> · DLC {l.dlc}</>}
                          {l.dedicated_to_order_id && <span className="text-[#C05C47] ml-1">· dédié cmd</span>}
                        </SelectItem>
                      ))}
                      <SelectItem value="manual">
                        <em>Créer un nouveau lot manuellement…</em>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {sourceLotId && sourceLotId !== "manual" && (
                  <div className="text-[10px] text-[#575A57] mt-1.5">
                    ✓ Transfert propre depuis {sourceDepotName} — n° lot et DLC repris automatiquement.
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">Slot</Label>
                <Select
                  value={newForm.emplacement_number || "none"}
                  onValueChange={(v) => setNewForm((f) => ({ ...f, emplacement_number: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="new-lot-slot"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="none">— Aucun —</SelectItem>
                    {(distributor.emplacements || []).map((n) => (
                      <SelectItem key={n} value={String(n)}>Slot {n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Quantité *</Label>
                <Input
                  type="number"
                  min="1"
                  value={newForm.quantity}
                  onChange={(e) => setNewForm((f) => ({ ...f, quantity: e.target.value }))}
                  data-testid="new-lot-qty"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">
                  N° lot {sourceLotId && sourceLotId !== "manual" ? <span className="text-[10px] text-[#1F4E3D]">(depuis {sourceDepotName})</span> : "*"}
                </Label>
                <Input
                  value={newForm.lot_number}
                  onChange={(e) => setNewForm((f) => ({ ...f, lot_number: e.target.value }))}
                  placeholder={sourceLotId && sourceLotId !== "manual" ? "" : "Ex: 15226 ou L-03-260815"}
                  className="font-mono"
                  data-testid="new-lot-number"
                  readOnly={!!(sourceLotId && sourceLotId !== "manual")}
                />
              </div>
              <div>
                <Label className="mb-1.5 block">
                  DLC {sourceLotId && sourceLotId !== "manual" ? <span className="text-[10px] text-[#1F4E3D]">(depuis {sourceDepotName})</span> : ""}
                </Label>
                <Input
                  type="date"
                  value={newForm.dlc}
                  onChange={(e) => setNewForm((f) => ({ ...f, dlc: e.target.value }))}
                  data-testid="new-lot-dlc"
                  readOnly={!!(sourceLotId && sourceLotId !== "manual")}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} disabled={newSaving}>Annuler</Button>
            <Button
              onClick={submitNewLot}
              disabled={newSaving || !newForm.product_id}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="new-lot-save"
            >
              {newSaving ? "…" : "Créer le lot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 87be — Dialog Sortie Partenaire (vente directe / prêt / casse signalée) */}
      <Dialog open={partnerOpen} onOpenChange={setPartnerOpen}>
        <DialogContent className="max-w-md" data-testid="partner-withdrawal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-[#5847A6]" /> Sortie partenaire
            </DialogTitle>
            <DialogDescription>
              {partnerForm.target_lot && (
                <>
                  <strong>{productMap[partnerForm.target_lot.product_id]?.name}</strong>
                  <br/>
                  Lot <span className="font-mono">{partnerForm.target_lot.lot_number}</span>
                  {partnerForm.target_lot.emplacement_number && <> · Slot {partnerForm.target_lot.emplacement_number}</>}
                  {" · "}Stock actuel : <strong>{partnerForm.target_lot.quantity}</strong>
                </>
              )}
              <br/>
              <span className="text-xs italic">Le stock sera décrémenté + mouvement OUT créé pour la HACCP.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Partenaire *</Label>
              <Select
                value={partnerForm.partner_id}
                onValueChange={(v) => setPartnerForm({ ...partnerForm, partner_id: v })}
              >
                <SelectTrigger data-testid="pw-partner"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1.5 block">Quantité *</Label>
                <Input
                  type="number"
                  min="1"
                  max={partnerForm.target_lot?.quantity}
                  value={partnerForm.quantity}
                  onChange={(e) => setPartnerForm({ ...partnerForm, quantity: e.target.value })}
                  data-testid="pw-quantity"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Raison *</Label>
                <Select
                  value={partnerForm.reason}
                  onValueChange={(v) => setPartnerForm({ ...partnerForm, reason: v })}
                >
                  <SelectTrigger data-testid="pw-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VENTE_DIRECTE">Vente directe</SelectItem>
                    <SelectItem value="EMPRUNT">Emprunt</SelectItem>
                    <SelectItem value="ECHANTILLON">Échantillon</SelectItem>
                    <SelectItem value="CASSE">Casse signalée</SelectItem>
                    <SelectItem value="VOL">Vol signalé</SelectItem>
                    <SelectItem value="AUTRE">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes</Label>
              <Input
                value={partnerForm.notes}
                onChange={(e) => setPartnerForm({ ...partnerForm, notes: e.target.value })}
                placeholder="ex: client comptoir 12h30 · Twint direct partenaire"
                data-testid="pw-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartnerOpen(false)} disabled={partnerSaving}>Annuler</Button>
            <Button
              onClick={savePartnerWithdrawal}
              disabled={partnerSaving || !partnerForm.partner_id}
              className="bg-[#5847A6] hover:bg-[#4A3B8C]"
              data-testid="pw-save"
            >
              {partnerSaving ? "…" : "Enregistrer le retrait"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Caméra scan */}
      <BarcodeScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={(code) => { setCameraOpen(false); resolveScan(code); }}
      />
    </div>
  );
}

function StatChip({ label, value, variant = "default", testid }) {
  const color = variant === "warn"
    ? "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]"
    : "bg-[#F4F4F0] border-[#E5E5E0] text-[#1A1C1A]";
  return (
    <div className={`border rounded-md p-2 ${color}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-heading font-medium">{value}</div>
    </div>
  );
}

function SlotCard({ slot, capacity, used, lots, productMap, onAdjust, onEdit, onEmpty, onPartner, onAddLot, partnersAvailable }) {
  const fillRatio = capacity ? Math.min(100, Math.round(100 * used / capacity)) : null;
  const slotLabel = slot === "none" ? "Sans slot" : `Slot ${slot}`;
  return (
    <div
      className={`bg-white border rounded-lg overflow-hidden ${lots.length === 0 ? "border-dashed border-[#E5E5E0]" : "border-[#E5E5E0]"}`}
      data-testid={`comptoir-slot-${slot}`}
    >
      {/* Slot header */}
      <div className="bg-[#1F4E3D] text-white px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-base">{slotLabel}</span>
          {capacity != null && (
            <Badge variant="outline" className="bg-white/10 border-white/30 text-white text-[10px]">
              {used}/{capacity}{fillRatio != null && ` · ${fillRatio}%`}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onAddLot}
          className="text-white hover:bg-white/10 h-7 px-2"
          data-testid={`add-lot-slot-${slot}`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Lot
        </Button>
      </div>

      {/* Lots dans ce slot */}
      <div className="divide-y divide-[#E5E5E0]">
        {lots.length === 0 ? (
          <div className="p-3 text-center text-xs text-[#A3A6A3] italic">
            Slot vide
          </div>
        ) : lots.map((lot) => (
          <LotRow
            key={lot.id}
            lot={lot}
            product={productMap[lot.product_id]}
            onAdjust={onAdjust}
            onEdit={onEdit}
            onEmpty={onEmpty}
            onPartner={onPartner}
            partnersAvailable={partnersAvailable}
          />
        ))}
      </div>
    </div>
  );
}

function LotRow({ lot, product, onAdjust, onEdit, onEmpty, onPartner, partnersAvailable }) {
  const daysLeft = useMemo(() => {
    if (!lot.dlc) return null;
    const d = new Date(lot.dlc);
    return Math.floor((d - new Date()) / (1000 * 60 * 60 * 24));
  }, [lot.dlc]);

  let dlcColor = "bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F]";
  if (daysLeft != null) {
    if (daysLeft < 0) dlcColor = "bg-[#FCEEEA] border-[#E8C1B4] text-[#C05C47]";
    else if (daysLeft <= 1) dlcColor = "bg-[#FCEEEA] border-[#E8C1B4] text-[#C05C47]";
    else if (daysLeft <= 3) dlcColor = "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]";
  }

  return (
    <div className="p-2.5">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium leading-tight truncate" title={product?.name}>
            {product?.name || "Produit inconnu"}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className="text-[10px] font-mono text-[#575A57] flex items-center gap-0.5">
              <Hash className="w-2.5 h-2.5" />{lot.lot_number}
            </span>
            {lot.dlc && (
              <Badge variant="outline" className={`${dlcColor} text-[10px] font-mono`}>
                <Calendar className="w-2.5 h-2.5 mr-0.5" />
                {new Date(lot.dlc).toLocaleDateString("fr-CH")}
                {daysLeft != null && (
                  <span className="ml-1 opacity-75">
                    ({daysLeft < 0 ? `${Math.abs(daysLeft)}j périmé` : `J+${daysLeft}`})
                  </span>
                )}
              </Badge>
            )}
            {lot.barquette_index && (
              <Badge variant="outline" className="text-[10px] font-mono bg-[#F4F4F0]">
                B{lot.barquette_index}
              </Badge>
            )}
            {daysLeft != null && daysLeft < 0 && (
              <span className="text-[10px] text-[#C05C47] flex items-center gap-0.5">
                <AlertTriangle className="w-3 h-3" /> Retirer
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Boutons gros doigt pour tablette */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAdjust(lot, -1)}
          disabled={(lot.quantity || 0) <= 0}
          className="flex-1 h-10 text-base font-bold border-[#C05C47]/30 hover:bg-[#FCEEEA] hover:border-[#C05C47]"
          data-testid={`lot-minus-${lot.id}`}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <button
          type="button"
          onClick={() => onEdit(lot)}
          className="flex-1 h-10 px-2 border border-[#1F4E3D] bg-[#1F4E3D]/5 hover:bg-[#1F4E3D]/10 rounded-md transition-colors flex items-center justify-center gap-1.5"
          data-testid={`lot-qty-${lot.id}`}
          title="Cliquez pour saisir la quantité exacte"
        >
          <span className="text-xl font-mono font-bold text-[#1F4E3D]">{lot.quantity || 0}</span>
          <Edit3 className="w-3 h-3 text-[#1F4E3D]/60" />
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAdjust(lot, +1)}
          className="flex-1 h-10 text-base font-bold border-[#2D6A4F]/30 hover:bg-[#EBF2E9] hover:border-[#2D6A4F]"
          data-testid={`lot-plus-${lot.id}`}
        >
          <Plus className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEmpty(lot)}
          className="h-10 px-2 text-[#A3A6A3] hover:text-[#C05C47] hover:bg-[#FCEEEA]"
          title="Marquer ce lot comme vide"
          data-testid={`lot-empty-${lot.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
        {partnersAvailable && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPartner(lot)}
            className="h-10 px-2 text-[#A3A6A3] hover:text-[#5847A6] hover:bg-[#E8E4F8]"
            title="Sortie partenaire (vente directe, emprunt, échantillon)"
            data-testid={`lot-partner-${lot.id}`}
          >
            <UserMinus className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
